import { Download, FileText, FileAudio, FileVideo } from 'lucide-react'
import type { ChatAttachment } from '../models/chat.model'
import { useAttachmentSrc } from '@/features/messenger/hooks/useAttachmentSrc'
import { AttachmentLink } from '@/features/messenger/private-attachment-ui'
import { downloadAttachment } from '@/features/messenger/services/download'
import { documentType } from '../services/document-type'

/**
 * Per-kind asset bubble used inside a chat message.
 *
 *   image    → tap-to-open preview (object-cover thumbnail)
 *   audio    → native <audio controls> with file name above
 *   video    → native <video controls> sized to the bubble
 *   document → type tile + file name + size; the receiver's also downloads
 *
 * The component is intentionally dumb: it receives a fully-resolved
 * attachment (incl. `url`) and renders it. The caller decides whether the
 * URL is an object URL (mock) or a real CDN link (when the backend lands).
 * A private one (VAPT 2026-10) names the file but does not open it: shown
 * through a link signed for this room (useAttachmentSrc), opened through
 * AttachmentLink, and a document saved through downloadAttachment.
 */

interface ChatAttachmentBubbleProps {
  attachment: ChatAttachment
  isMe: boolean
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function ChatAttachmentBubble({ attachment, isMe }: ChatAttachmentBubbleProps) {
  const { kind, url, name, size } = attachment
  const src = useAttachmentSrc(url)
  const bubbleSide = isMe ? 'rounded-br-sm' : 'rounded-bl-sm'

  if (kind === 'image') {
    return (
      <AttachmentLink
        href={url}
        className={`block overflow-hidden rounded-2xl ${bubbleSide} max-w-[260px]`}
      >
        <img src={src} alt={name} className="w-full h-auto max-h-[240px] object-cover" />
        <div className="bg-slate-100 dark:bg-muted px-3 py-1.5 text-[10px] text-slate-500 dark:text-muted-foreground truncate">
          {name}
        </div>
      </AttachmentLink>
    )
  }

  if (kind === 'video') {
    return (
      <div
        className={`overflow-hidden rounded-2xl ${bubbleSide} bg-slate-100 dark:bg-muted max-w-[280px]`}
      >
        <video
          src={src}
          controls
          playsInline
          className="w-full h-auto max-h-[260px] bg-black"
        />
        <div className="px-3 py-1.5 text-[10px] text-slate-500 dark:text-muted-foreground truncate flex items-center gap-1.5">
          <FileVideo className="w-3 h-3" />
          <span className="truncate">{name}</span>
          <span className="ml-auto shrink-0">{formatBytes(size)}</span>
        </div>
      </div>
    )
  }

  if (kind === 'audio') {
    return (
      <div
        className={`rounded-2xl ${bubbleSide} bg-slate-100 dark:bg-muted px-3 py-2 max-w-[280px] w-full`}
      >
        <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-foreground mb-1.5">
          <FileAudio className="w-3.5 h-3.5 text-amber-500" />
          <span className="truncate flex-1">{name}</span>
          <span className="text-[10px] text-slate-400 dark:text-muted-foreground shrink-0">
            {formatBytes(size)}
          </span>
        </div>
        <audio src={src} controls className="w-full h-8" />
      </div>
    )
  }

  // document — its type, name and size, as the messenger's file card shows
  // them. Download is the receiver's alone, as in the messenger (BUG-000822):
  // the sender picked this file off their own disk and still has it.
  const { ext, color } = documentType(name)
  const card = `flex items-center gap-3 rounded-2xl ${bubbleSide} bg-slate-100 dark:bg-muted px-3 py-2.5 max-w-[280px]`
  const details = (
    <>
      <span
        className="w-9 h-9 rounded-lg flex flex-col items-center justify-center shrink-0 text-white"
        style={{ backgroundColor: color }}
      >
        <FileText className="w-3.5 h-3.5" />
        <span className="mt-0.5 text-[8px] font-bold leading-none">{ext}</span>
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-medium text-slate-800 dark:text-foreground truncate">
          {name}
        </span>
        <span className="block text-[10px] tabular-nums text-slate-400 dark:text-muted-foreground">
          {formatBytes(size)} · {ext}
        </span>
      </span>
    </>
  )

  if (isMe) return <div className={card}>{details}</div>

  // A button that saves the file, not a link. As a link it opened a tab:
  // `download` is ignored cross-origin, and a private file's tab opens blank
  // inside the click to wait for its signed link, which then downloaded and
  // left the tab standing on about:blank (BUG-000823). The bytes are fetched
  // and saved from a same-origin blob instead, as the messenger does.
  return (
    <button
      type="button"
      onClick={() => void downloadAttachment(url, name)}
      className={`${card} text-left hover:bg-slate-200 dark:hover:bg-muted/80 transition-colors`}
    >
      {details}
      <Download className="w-3.5 h-3.5 text-slate-400 dark:text-muted-foreground shrink-0" />
    </button>
  )
}
